/* Reading a guest's QR with the device camera.

   Uses the browser's own BarcodeDetector where it exists (Chrome, Android —
   hardware accelerated) and falls back to jsQR everywhere else. The camera
   needs a secure context: it works on the deployed HTTPS site and on
   localhost, but not over plain http to another machine. */

import jsQR from 'jsqr';

const SCAN_INTERVAL_MS = 120;

export class CameraScanner {
  constructor(video, { onCode, onError } = {}) {
    this.video = video;
    this.onCode = onCode;
    this.onError = onError;
    this.stream = null;
    this.timer = null;
    this.canvas = document.createElement('canvas');
    this.ctx = this.canvas.getContext('2d', { willReadFrequently: true });
    this.detector = null;
    this.lastCode = '';
    this.lastAt = 0;
  }

  get running() { return !!this.stream; }

  static get supported() {
    return !!(navigator.mediaDevices?.getUserMedia) && window.isSecureContext;
  }

  async start() {
    if (this.stream) return;
    if (!CameraScanner.supported) {
      throw new Error(window.isSecureContext
        ? 'This browser has no camera API.'
        : 'The camera needs https — open the deployed site, not plain http.');
    }
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' },
                 width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
    } catch (err) {
      throw new Error(err.name === 'NotAllowedError'
        ? 'Camera permission was denied — allow it in the address bar.'
        : `Could not open the camera: ${err.message}`);
    }
    this.video.srcObject = this.stream;
    this.video.setAttribute('playsinline', '');
    await this.video.play();

    if ('BarcodeDetector' in window) {
      try {
        const formats = await window.BarcodeDetector.getSupportedFormats();
        if (formats.includes('qr_code')) {
          this.detector = new window.BarcodeDetector({ formats: ['qr_code'] });
        }
      } catch { this.detector = null; }
    }
    this.timer = setInterval(() => this.tick(), SCAN_INTERVAL_MS);
  }

  stop() {
    clearInterval(this.timer);
    this.timer = null;
    this.stream?.getTracks().forEach(t => t.stop());
    this.stream = null;
    this.video.srcObject = null;
  }

  /** Ignore the same code for a moment so one badge is not read ten times. */
  accept(text) {
    const now = Date.now();
    if (text === this.lastCode && now - this.lastAt < 2500) return;
    this.lastCode = text;
    this.lastAt = now;
    this.onCode?.(text);
  }

  async tick() {
    const v = this.video;
    if (!v || v.readyState < 2 || !v.videoWidth) return;
    try {
      if (this.detector) {
        const hits = await this.detector.detect(v);
        if (hits.length) this.accept(hits[0].rawValue);
        return;
      }
      // Downscale before handing pixels to jsQR: full HD frames are far more
      // work than the decoder needs, and the loop has to keep up with the
      // camera rather than with the screen.
      const scale = Math.min(1, 640 / v.videoWidth);
      this.canvas.width = Math.round(v.videoWidth * scale);
      this.canvas.height = Math.round(v.videoHeight * scale);
      this.ctx.drawImage(v, 0, 0, this.canvas.width, this.canvas.height);
      const img = this.ctx.getImageData(0, 0, this.canvas.width, this.canvas.height);
      const hit = jsQR(img.data, img.width, img.height,
                       { inversionAttempts: 'dontInvert' });
      if (hit?.data) this.accept(hit.data);
    } catch (err) {
      this.onError?.(err);
    }
  }
}
